/**
 * @lines98/client/components/badge-strip — ГРИД значков-ачивок: ОДИН грид иконок-
 * медальонов ФИКС-размера (icon-only, БЕЗ подписи-имени). Свёртка до 1 ряда + раскрытие
 * (1 ряд ↔ все ряды) задаёт ЭКРАН-потребитель (он флипает класс `--open` на контейнере);
 * этот примитив несёт только грид одинаковых значков + деталь-попап по тапу.
 *
 * ЗАЧЕМ / WHY THIS EXISTS (owner verbatim 2026-06-21): прежняя «preview(5) + полный грид
 * с именами» (badge-grid previewCount) owner'ом ОТКЛОНЕНА. Owner: «значки такие же как
 * сейчас на ачивках (до раскрытия)… при раскрытии квадратики НЕ должны расти, а просто
 * появляются доп. полосы с остальными ачивками такого же размера». Поэтому это ОТДЕЛЬНЫЙ
 * примитив с другой моделью: НЕ preview-N + full-grid, а ОДИН грид одинаковых icon-only
 * значков фикс-размера (`repeat(auto-fill, <фикс-токен>)` — число по ширине, НЕ фикс-число).
 * Размер значка ИНВАРИАНТЕН к состоянию (Δ0 свёрнуто/раскрыто); сворачивание до 1 ряда
 * (overflow:hidden + max-height) держит контейнер-экран.
 *
 * v4 (owner-approved 2026-06-21): раскрытие переключает ФУТЕР-кнопка плашки ачивок
 *   («Показать все ▾» / «Свернуть ▴»), которую монтирует ЭКРАН (profile-screen) — НЕ
 *   боковая каретка (disclosure-caret больше НЕ используется Профилем для ачивок). Этот
 *   примитив о механизме раскрытия НЕ знает (раскрытие = класс `--open` от экрана).
 *
 * SCOPE: грид icon-only значков (тот же медальон-плейсхолдер, что badge-grid, БЕЗ
 *   подписи-имени) + деталь = АНКЕР-ПОПАП у значка (общий модуль `components/
 *   achievement-detail` на стволе `lib/anchored-popover` — НЕ локальная модалка). NOT IN
 *   SCOPE: раскрытие/сворачивание (ЭКРАН флипает `--open` по футер-кнопке), триггеры
 *   разблокировки (server-side), данные каталога (lib/achievement-catalog single source),
 *   вид/анкер/тумблер детали (achievement-detail + anchored-popover).
 *
 * Server-truth (I-3): открыта ли ачивка — серверная правда (`unlockedIds`); примитив
 * только рисует (открытые подсвечены, закрытые — приглушённый силуэт).
 *
 * Cross-references: owner-approved Профиль v4 (2026-06-21) + fe-achievement-detail-popover.md
 *   (анкер-попап детали); screens/profile-screen (потребитель: монтирует грид + футер-кнопку
 *   раскрытия, флипает --open); lib/achievement-catalog (ACHIEVEMENT_DISPLAY);
 *   components/achievement-detail (деталь — ОДИН модуль, общий с badge-grid);
 *   components/badge-strip.css (layout-plan + algebra).
 */

import './badge-strip.css';

import { createAchievementDetail } from './achievement-detail.js';
import { ACHIEVEMENT_DISPLAY } from '../lib/achievement-catalog.js';
import { t } from '../lib/i18n/index.js';

export interface BadgeStripOptions {
  /**
   * Множество разблокированных id (server-truth `progression.achievements[].achievementId`).
   * Значки из этого множества подсвечены; остальные — приглушённый силуэт.
   */
  readonly unlockedIds: ReadonlySet<string>;
  /**
   * Поставщик ГРАНИЦЫ облака-детали (owner-directive 2026-06-21): прямоугольник, за который
   * деталь ачивки НЕ выезжает (Профиль передаёт таблицу). Прокидывается в achievement-detail.
   */
  readonly getBoundary?: () => DOMRect | null;
}

export interface BadgeStripHandle {
  /** Корневой элемент — грид значков (.badge-strip-grid). Потребитель вставляет его. */
  readonly element: HTMLElement;
  /** Обновить множество разблокированных (после ресинка server-truth). */
  update(unlockedIds: ReadonlySet<string>): void;
  /** Снять попап детали. */
  destroy(): void;
}

/**
 * Создать грид значков-ачивок. Возвращает handle; потребитель вставляет `handle.element`
 * (= `.badge-strip-grid`) в свой контейнер `.profile-achievements` рядом с каретным
 * триггером и управляет свёрткой (1 ряд) флипом класса `--open` на контейнере.
 */
export function createBadgeStrip(opts: BadgeStripOptions): BadgeStripHandle {
  let unlocked = opts.unlockedIds;

  // ОДИН грид icon-only значков ФИКС-размера (repeat(auto-fill, <токен>) — число по
  // ширине). Сворачивание до 1 ряда (max-height + overflow) держит CSS контейнера-экрана.
  const grid = document.createElement('div');
  grid.className = 'badge-strip-grid';
  grid.setAttribute('role', 'list');
  grid.setAttribute('aria-label', t('game.achievements.gridTitle'));

  // Деталь ачивки — общий анкер-попап (тумблер по значку; один экземпляр на грид).
  // Граница облака (owner 2026-06-21) — прокидывается от экрана-потребителя (Профиль → таблица).
  const detail = createAchievementDetail({ getBoundary: opts.getBoundary });

  const cellByIndex: HTMLButtonElement[] = [];
  for (const def of ACHIEVEMENT_DISPLAY) {
    const cell = document.createElement('button');
    cell.type = 'button';
    cell.className = 'badge-strip-cell';
    cell.dataset.id = def.id;
    cell.setAttribute('role', 'listitem');
    // ТОЛЬКО медальон-иконка (как у ачивок до раскрытия), БЕЗ подписи-имени (AC-14).
    // Буква проставляется в applyUnlocked (скрытые до разблокировки маскируются «?»).
    cell.innerHTML = `
      <span class="badge-strip-medal" aria-hidden="true">
        ${BADGE_MEDAL_GLYPH}
        <span class="badge-strip-letter"></span>
      </span>
    `;
    // Тап по значку → тумблер анкер-попапа у ЭТОГО значка (повторный тап закрывает,
    // другой значок переносит попап). aria-haspopup/aria-expanded ставит ствол попапа.
    cell.addEventListener('click', () => detail.toggle(cell, def, unlocked.has(def.id)));
    grid.appendChild(cell);
    cellByIndex.push(cell);
  }

  function applyUnlocked(): void {
    for (let i = 0; i < ACHIEVEMENT_DISPLAY.length; i++) {
      const def = ACHIEVEMENT_DISPLAY[i]!;
      const isUnlocked = unlocked.has(def.id);
      const cell = cellByIndex[i]!;
      const masked = def.hidden && !isUnlocked;
      const shownName = masked ? '???' : def.name;
      cell.classList.toggle('badge-strip-cell--unlocked', isUnlocked);
      cell.classList.toggle('badge-strip-cell--locked', !isUnlocked);
      cell.classList.toggle('badge-strip-cell--hidden', masked);
      cell.classList.add(`badge-strip-cell--${def.rarity}`);
      cell.querySelector('.badge-strip-letter')!.textContent = masked ? '?' : firstLetter(def.name);
      cell.setAttribute(
        'aria-label',
        t('game.achievements.statusMoreAria', {
          name: shownName,
          status: isUnlocked ? t('game.achievements.unlocked') : t('game.achievements.locked'),
        }),
      );
    }
  }

  applyUnlocked();

  return {
    element: grid,
    update(next): void {
      unlocked = next;
      applyUnlocked();
    },
    destroy(): void {
      detail.destroy();
    },
  };
}

// =============================================================================
// Helpers
// =============================================================================

/** Гранёный медальон-плейсхолдер значка (тот же глиф, что badge-grid; свопабельно на art). */
const BADGE_MEDAL_GLYPH = `<svg class="badge-strip-svg" viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false"><circle cx="12" cy="11" r="8" fill="var(--meta-gold-fill)" stroke="var(--meta-metal-edge)" stroke-width="1.4"/><path d="M12 3l1.6 3.4 3.7.5-2.7 2.6.7 3.7L12 11.9 8.7 13.7l.7-3.7L6.7 7.4l3.7-.5Z" fill="var(--meta-gem-facet)" stroke="var(--meta-metal-edge)" stroke-width="0.7" stroke-linejoin="round" opacity="0.85"/><path d="M8 18l1.5 3M16 18l-1.5 3" stroke="var(--meta-metal-edge)" stroke-width="1.2"/></svg>`;

function firstLetter(name: string): string {
  return (name.trim()[0] ?? '?').toUpperCase();
}
